import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Description } from './PredictiveStatesSection.styles';
import IndividualReportDetailsModal from '../../../components/IndividualReportDetailsModal/IndividualReportDetailsModal';
import IndividualReportSummaryList from '../../../components/IndividualReportSummaryList/IndividualReportSummaryList';
import { useDialog } from '../../../hooks';
import { useFilters } from '../../../hooks/useFilters';
import { IIndividualStates, IIndividualStatesTopic } from '../../../services/individualReport';
import Legend from '../components/Legend/Legend';
import PageSection from '../components/PageSection/PageSection';

interface IPredictiveStatesSection {
  individualStates: IIndividualStates;
}

const PredictiveStatesSection: FC<IPredictiveStatesSection> = ({ individualStates }) => {
  const { ref, handleCloseDialog, handleOpenDialog } = useDialog();
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { t } = useTranslation();

  const [topic, setTopic] = useState<IIndividualStatesTopic | null>(null);

  const { description, group_description, topics } = individualStates ?? {};

  const parsedDescription = description.replaceAll('<br>', '\n');
  const parsedGroupDescription = group_description
    ? group_description.replaceAll('<br>', '\n')
    : '';

  const tabs = useMemo(
    () => [
      {
        name: t('results.report.tabs.results.label'),
        tooltip: t('results.report.tabs.results.tooltip'),
        value: 'results',
      },
      {
        name: t('results.report.tabs.group.label'),
        tooltip: t('results.report.tabs.group.tooltip'),
        value: 'group',
      },
    ],
    [t]
  );

  const getFilters = useMemo(() => {
    const individualStates = currentFilters?.individualStates as string | null;

    if (individualStates) {
      const filters = JSON.parse(individualStates) as { mode: 'results' | 'group' };

      return {
        mode: filters?.mode || 'results',
      };
    }
  }, [currentFilters?.individualStates]);

  const mode = useMemo(() => getFilters?.mode || 'results', [getFilters?.mode]);

  const handleOpenModal = (topic: IIndividualStatesTopic) => {
    setTopic(topic);
    handleOpenDialog();
  };

  return (
    <>
      <PageSection ariaLabel="individual-states">
        <PageSection.Header>
          <PageSection.Header.Title tag="h3" title={t('results.report.predictiveStates.title')} />
          <PageSection.Header.Tabs
            activeTab={mode}
            tabs={tabs}
            setTab={(tab: string) => applyAndMergeFilters('individualStates', 'mode', tab)}
          />
        </PageSection.Header>
        <PageSection.Content>
          <Description id="Description" as="p">
            {mode === 'results' ? parsedDescription : parsedGroupDescription}
          </Description>
          <Legend />
          <IndividualReportSummaryList
            data={topics}
            mode={mode}
            type="topics"
            onClick={handleOpenModal}
          />
        </PageSection.Content>
      </PageSection>
      <IndividualReportDetailsModal
        data={topic}
        type="topic"
        onClose={handleCloseDialog}
        {...{ ref }}
      />
    </>
  );
};

export default PredictiveStatesSection;
